// Shared-password site gate (frontend-soft).
//
// The whole app (landing, feasibility form, admin) sits behind a
// single shared password — EXCEPT the public report pages at /r/<uuid>
// and the /api/r/<uuid> JSON endpoint, which stay open so client
// links + the Moya Copilot keep working.
//
// The password IS the admin key (one shared secret). It's validated
// against POST /api/site-unlock so the secret is never baked into the
// JS bundle, then cached in localStorage. The same cached value is
// reused as the admin key for admin API calls — so there's no second
// prompt and no ?key=… in the URL.

const SITE_KEY_STORAGE = 'massing_site_key_v1'

export function getStoredSiteKey(): string | null {
  // Migration: capture a legacy `?key=…` URL (old admin bookmark)
  // into storage + strip it from the address bar so the app works
  // password-prompt-style from then on.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('key')
    if (fromUrl) {
      localStorage.setItem(SITE_KEY_STORAGE, fromUrl)
      const url = new URL(window.location.href)
      url.searchParams.delete('key')
      window.history.replaceState({}, '', url.pathname + url.search + url.hash)
      return fromUrl
    }
    return localStorage.getItem(SITE_KEY_STORAGE)
  } catch {
    return null
  }
}

export function setStoredSiteKey(key: string): void {
  try {
    localStorage.setItem(SITE_KEY_STORAGE, key)
  } catch {
    /* private mode — session-only for this tab; non-fatal */
  }
}

export function clearStoredSiteKey(): void {
  try {
    localStorage.removeItem(SITE_KEY_STORAGE)
  } catch {
    /* ignore */
  }
}

/** Validate a candidate password against the backend. Returns true
 *  when the server accepts it (matches ADMIN_API_KEY). */
export async function validateSiteKey(key: string): Promise<boolean> {
  try {
    const res = await fetch('/api/site-unlock', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ password: key }),
    })
    return res.ok
  } catch {
    return false
  }
}
